import React, { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  buildBatchOptions,
  findBatchById,
} from "features/immunizations/utils/vaccineBatches";

import style from "../AddVaccineForm.module.css";
import { SearchableSelectField } from "./SearchableSelectField";
import { TextField } from "./TextField";

/*
 * Batch No: picked from the registry, or typed (KIN-1740).
 *
 * Phase 2 §4.4.4 makes the batch the entry point — picking one fills the product, dose
 * and trade name — and Dalen names mistyped batch numbers as a live source of PCRS
 * problems. So the default is the list.
 *
 * Typing stays reachable rather than being removed, and not only for Manual mode. The
 * registry is HSE's national list plus this practice's own private batches, and a vial
 * that is in neither still has to be recordable: a batch the loader could not read, one
 * delivered before the list caught up, a correction to an old record. Without a way back
 * to free text that dose has nowhere to go. Switching to typing drops the link, which is
 * what makes the number free text again on the server too.
 */
export const BatchNumberField = ({
  batches = [],
  isLoadingBatches = false,
  recordedBatch = null,
}) => {
  const { watch, setValue } = useFormContext();
  const selectedBatchId = watch("vaccineBatchId");
  const vaccineCodeId = watch("vaccineCodeId");

  // Starts on the list unless the record being edited has no batch linked and already
  // carries a typed number — reopening that one on the picker would present a blank
  // field over a dose that plainly has a batch number.
  const [isTypedByHand, setIsTypedByHand] = useState(
    () => !recordedBatch && !!watch("batchNo")
  );

  const options = useMemo(
    () => buildBatchOptions({ batches, vaccineCodeId, recordedBatch }),
    [batches, vaccineCodeId, recordedBatch]
  );

  const selectedBatch = findBatchById(batches, selectedBatchId);

  const switchToTyping = () => {
    // The link goes, the number stays. The clinician is correcting a batch the list got
    // wrong or does not hold, and making them retype a number that is already right is
    // the mistyping this ticket exists to stop.
    setValue("vaccineBatchId", "", { shouldValidate: false });
    setIsTypedByHand(true);
  };

  const switchToList = () => {
    setValue("batchNo", "", { shouldValidate: true });
    setValue("expiryDate", "", { shouldValidate: true });
    setIsTypedByHand(false);
  };

  // Nothing to pick from: the list is empty, or it failed to load. Typing is the only
  // honest option, and offering an empty dropdown instead would be a dead end.
  const hasListToOffer = options.length > 0 || isLoadingBatches;
  const isTyping = isTypedByHand || !hasListToOffer;

  return (
    <div className={style.batchField}>
      {isTyping ? (
        <TextField name="batchNo" label="Batch No" required />
      ) : (
        <SearchableSelectField
          name="vaccineBatchId"
          label="Batch No"
          options={options}
          placeholder={
            isLoadingBatches ? "Loading batches..." : "Search batches..."
          }
          required
        />
      )}

      {!!hasListToOffer && (
        <button
          type="button"
          className={style.batchEntryToggle}
          onClick={isTyping ? switchToList : switchToTyping}
        >
          {isTyping ? "Choose from the batch list" : "Enter a batch number manually"}
        </button>
      )}

      {/* Said here as well as on the option, because by the time it is chosen the option
          text is no longer on screen and the claim is made later, from the record. */}
      {selectedBatch?.isPublic === false && (
        <p className={style.batchFieldNote}>
          Private stock — this dose must not be claimed from PCRS.
        </p>
      )}
    </div>
  );
};
